import {Component, inject, ChangeDetectionStrategy} from "@angular/core"
import {isInstanceOf} from "@cm/utils"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {
    ImageOperator,
    MeshNode,
    InputNode,
    ExportNode,
    ObjectNode,
    isValueLikeNode,
    MeshCurve,
    MathOperator,
    ImageRgbCurve,
    TemplateNode,
    Camera,
    PostProcessRender,
    SceneProperties,
    Annotation,
    ConfigGroup,
    ConfigVariant,
    TemplateInstance,
    TemplateReference,
    RigidRelation,
    MeshDecal,
    Group,
    PlaneGuide,
    PointGuide,
    HdriLight,
    AreaLight,
    LightPortal,
    Render,
    OverlayMaterialColor,
    MaterialReference,
    LodType,
    DataObjectReference,
    DimensionGuides,
    PlanarCurve,
    TransformMatrix,
    DistanceTexture,
    GeometryGraphNode,
    TransformToValue,
    BlendMaterialSlot,
    FontMaterial,
    ExtractMaterialSlot,
    PointLight,
    SunLight,
} from "@cm/template-nodes"
import {Mattress} from "@cm/mattress-nodes"
import {ImageOperatorInspectorComponent} from "@template-editor/components/inspectors/image-operator-inspector/image-operator-inspector.component"
import {MathOperatorInspectorComponent} from "@template-editor/components/inspectors/math-operator-inspector/math-operator-inspector.component"
import {MeshCurveInspectorComponent} from "@template-editor/components/inspectors/mesh-curve-inspector/mesh-curve-inspector.component"
import {MattressInspectorComponent} from "@app/template-editor/components/inspectors/mattress-inspector/mattress-inspector.component"
import {ObjectInspectorComponent} from "@template-editor/components/inspectors/object-inspector/object-inspector.component"
import {GenericInspectorComponent} from "@app/template-editor/components/inspectors/generic-inspector/generic-inspector.component"
import {CameraInspectorComponent} from "@app/template-editor/components/inspectors/camera-inspector/camera-inspector.component"
import {PostProcessRenderInspectorComponent} from "@app/template-editor/components/inspectors/post-process-render-inspector/post-process-render-inspector.component"
import {ScenePropertiesInspectorComponent} from "@app/template-editor/components/inspectors/scene-properties-inspector/scene-properties-inspector.component"
import {AnnotationInspectorComponent} from "@app/template-editor/components/inspectors/annotation-inspector/annotation-inspector.component"
import {ConfigGroupInspectorComponent} from "@app/template-editor/components/inspectors/config-group-inspector/config-group-inspector.component"
import {ConfigVariantInspectorComponent} from "@app/template-editor/components/inspectors/config-variant-inspector/config-variant-inspector.component"
import {TemplateInstanceInspectorComponent} from "@app/template-editor/components/inspectors/template-instance-inspector/template-instance-inspector.component"
import {TemplateReferenceInspectorComponent} from "@template-editor/components/inspectors/template-reference-inspector/template-reference-inspector.component"
import {RigidRelationInspectorComponent} from "@app/template-editor/components/inspectors/rigid-relation-inspector/rigid-relation-inspector.component"
import {MeshDecalInspectorComponent} from "@app/template-editor/components/inspectors/mesh-decal-inspector/mesh-decal-inspector.component"
import {GroupInspectorComponent} from "@app/template-editor/components/inspectors/group-inspector/group-inspector.component"
import {PlaneGuideInspectorComponent} from "@app/template-editor/components/inspectors/plane-guide-inspector/plane-guide-inspector.component"
import {PointGuideInspectorComponent} from "@app/template-editor/components/inspectors/point-guide-inspector/point-guide-inspector.component"
import {HdriLightInspectorComponent} from "@app/template-editor/components/inspectors/hdri-light-inspector/hdri-light-inspector.component"
import {AreaLightInspectorComponent} from "@app/template-editor/components/inspectors/area-light-inspector/area-light-inspector.component"
import {LightPortalInspectorComponent} from "@app/template-editor/components/inspectors/light-portal-inspector/light-portal-inspector.component"
import {MeshInspectorComponent} from "@app/template-editor/components/inspectors/mesh-inspector/mesh-inspector.component"
import {RenderInspectorComponent} from "@app/template-editor/components/inspectors/render-inspector/render-inspector.component"
import {InputInspectorComponent} from "@app/template-editor/components/inspectors/input-inspector/input-inspector.component"
import {ExportInspectorComponent} from "@app/template-editor/components/inspectors/export-inspector/export-inspector.component"
import {ValueInspectorComponent} from "@app/template-editor/components/inspectors/value-inspector/value-inspector.component"
import {OverlayMaterialColorInspectorComponent} from "@app/template-editor/components/inspectors/overlay-material-color-inspector/overlay-material-color-inspector.component"
import {MaterialReferenceInspectorComponent} from "@app/template-editor/components/inspectors/material-reference-inspector/material-reference-inspector.component"
import {LodTypeInspectorComponent} from "@app/template-editor/components/inspectors/lod-type-inspector/lod-type-inspector.component"
import {DataObjectReferenceInspectorComponent} from "@app/template-editor/components/inspectors/data-object-reference-inspector/data-object-reference-inspector.component"
import {DimensionGuidesInspectorComponent} from "@app/template-editor/components/inspectors/dimension-guides-inspector/dimension-guides-inspector.component"
import {ImageRgbCurveInspectorComponent} from "@app/template-editor/components/inspectors/image-rgb-curve-inspector/image-rgb-curve-inspector.component"
import {DistanceTextureInspectorComponent} from "@app/template-editor/components/inspectors/distance-texture-inspector/distance-texture-inspector.component"
import {PlanarCurveInspector} from "@template-editor/components/inspectors/planar-curve-inspector/planar-curve-inspector.component"
import {GeometryGraphNodeInspectorComponent} from "@template-editor/components/inspectors/geometry-graph-node-inspector/geometry-graph-node-inspector.component"
import {TransformMatrixInspectorComponent} from "@template-editor/components/inspectors/transform-matrix-inspector/transform-matrix-inspector.component"
import {TransformToValueInspectorComponent} from "@template-editor/components/inspectors/transform-to-value-inspector/transform-to-value-inspector.component"
import {toSignal} from "@angular/core/rxjs-interop"
import {switchMap, from, concatMap, delay, of, map, concat} from "rxjs"
import {BlendMaterialSlotInspectorComponent} from "@app/template-editor/components/inspectors/blend-material-slot-inspector/blend-material-slot-inspector.component"
import {FontMaterialInspectorComponent} from "@app/template-editor/components/inspectors/font-material-inspector copy/font-material-inspector.component"
import {ExtractMaterialSlotInspectorComponent} from "@template-editor/components/inspectors/extract-material-slot-inspector/extract-material-slot-inspector.component"
import {PointLightInspectorComponent} from "@app/template-editor/components/inspectors/point-light-inspector/point-light-inspector.component"
import {SunLightInspectorComponent} from "@app/template-editor/components/inspectors/sun-light-inspector/sun-light-inspector.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-inspector",
    templateUrl: "./template-inspector.component.html",
    styleUrl: "./template-inspector.component.scss",
    imports: [
        ObjectInspectorComponent,
        GenericInspectorComponent,
        CameraInspectorComponent,
        PostProcessRenderInspectorComponent,
        ScenePropertiesInspectorComponent,
        AnnotationInspectorComponent,
        ConfigGroupInspectorComponent,
        ConfigVariantInspectorComponent,
        TemplateInstanceInspectorComponent,
        RigidRelationInspectorComponent,
        MeshDecalInspectorComponent,
        GroupInspectorComponent,
        PlaneGuideInspectorComponent,
        PointGuideInspectorComponent,
        HdriLightInspectorComponent,
        AreaLightInspectorComponent,
        LightPortalInspectorComponent,
        MeshInspectorComponent,
        RenderInspectorComponent,
        InputInspectorComponent,
        ExportInspectorComponent,
        ValueInspectorComponent,
        OverlayMaterialColorInspectorComponent,
        MaterialReferenceInspectorComponent,
        LodTypeInspectorComponent,
        DataObjectReferenceInspectorComponent,
        MeshCurveInspectorComponent,
        MattressInspectorComponent,
        ImageOperatorInspectorComponent,
        MathOperatorInspectorComponent,
        DimensionGuidesInspectorComponent,
        ImageRgbCurveInspectorComponent,
        DistanceTextureInspectorComponent,
        PlanarCurveInspector,
        TemplateReferenceInspectorComponent,
        BlendMaterialSlotInspectorComponent,
        GeometryGraphNodeInspectorComponent,
        FontMaterialInspectorComponent,
        TransformMatrixInspectorComponent,
        TransformToValueInspectorComponent,
        ExtractMaterialSlotInspectorComponent,
        PointLightInspectorComponent,
        SunLightInspectorComponent,
    ],
})
export class TemplateInspectorComponent {
    private readonly sceneManagerService = inject(SceneManagerService)

    private lastEmittedSubList: TemplateNode[] = []
    readonly $selectedNodes = toSignal(
        this.sceneManagerService.selectedRootNodeParts$.pipe(
            map((nodeParts) => {
                const result = new Set(nodeParts.map((node) => node.templateNode))
                return [...result]
            }),
            switchMap((newList) => {
                let commonLength = 0
                const minLength = Math.min(this.lastEmittedSubList.length, newList.length)
                while (commonLength < minLength && this.lastEmittedSubList[commonLength] === newList[commonLength]) {
                    commonLength++
                }

                const emitSequence: TemplateNode[][] = []
                for (let i = commonLength; i < newList.length - 1; i++) {
                    emitSequence.push(newList.slice(0, i + 1))
                }
                emitSequence.push(newList)

                const [first, ...rest] = emitSequence

                return concat(
                    of(first).pipe(
                        switchMap((emitted) => {
                            this.lastEmittedSubList = emitted
                            return of(emitted)
                        }),
                    ),
                    from(rest).pipe(
                        concatMap((subList) =>
                            of(subList).pipe(
                                delay(10),
                                switchMap((emitted) => {
                                    this.lastEmittedSubList = emitted
                                    return of(emitted)
                                }),
                            ),
                        ),
                    ),
                )
            }),
        ),
    )

    isAnnotation = isInstanceOf(Annotation)
    isCamera = isInstanceOf(Camera)
    isConfigGroup = isInstanceOf(ConfigGroup)
    isConfigVariant = isInstanceOf(ConfigVariant)
    isMesh = isInstanceOf(MeshNode)
    isObject = isInstanceOf(ObjectNode)
    isPostProcessRender = isInstanceOf(PostProcessRender)
    isSceneProperties = isInstanceOf(SceneProperties)
    isTemplateInstance = isInstanceOf(TemplateInstance)
    isTemplateReference = isInstanceOf(TemplateReference)
    isRigidRelation = isInstanceOf(RigidRelation)
    isMeshDecal = isInstanceOf(MeshDecal)
    isMeshCurve = isInstanceOf(MeshCurve)
    isPlanarCurve = isInstanceOf(PlanarCurve)
    isGroup = isInstanceOf(Group)
    isPlaneGuide = isInstanceOf(PlaneGuide)
    isPointGuide = isInstanceOf(PointGuide)
    isHdriLight = isInstanceOf(HdriLight)
    isAreaLight = isInstanceOf(AreaLight)
    isPointLight = isInstanceOf(PointLight)
    isSunLight = isInstanceOf(SunLight)
    isLightPortal = isInstanceOf(LightPortal)
    isRender = isInstanceOf(Render)
    isInput = isInstanceOf(InputNode)
    isExport = isInstanceOf(ExportNode)
    isValue = isValueLikeNode
    isOverlayMaterialColor = isInstanceOf(OverlayMaterialColor)
    isBlendMaterialSlot = isInstanceOf(BlendMaterialSlot)
    isFontMaterial = isInstanceOf(FontMaterial)
    isExtractMaterialSlot = isInstanceOf(ExtractMaterialSlot)
    isMaterialReference = isInstanceOf(MaterialReference)
    isLodType = isInstanceOf(LodType)
    isDataObjectReference = isInstanceOf(DataObjectReference)
    isImageOperator = isInstanceOf(ImageOperator)
    isMathOperator = isInstanceOf(MathOperator)
    isImageRgbCurve = isInstanceOf(ImageRgbCurve)
    isDistanceTexture = isInstanceOf(DistanceTexture)
    isDimensionGuides = isInstanceOf(DimensionGuides)
    isGeometryGraphNode = isInstanceOf(GeometryGraphNode)
    isTransformMatrix = isInstanceOf(TransformMatrix)
    isTransformToValue = isInstanceOf(TransformToValue)
    isMattress = isInstanceOf(Mattress)
}
